// @ts-nocheck - init-trello.tsx bridges wac-web types (layout) and entries types which are incompatible at the boundary
import React from "react";

import { createRoot, hydrateRoot } from "react-dom/client";
import { StyleSheetManager } from "styled-components";

import { App } from "./entries/app";
import { PageProps } from "./layout";
import { Layout } from "./layout";
import { NotFoundPage } from "./pages/not-found-page";
// Single component for SSR/hydration path
const TrelloPageRenderer = (props: PageProps) => {
    if (props.isNotFound) {
        return <NotFoundPage pageModel={props.pageModel} />;
    }
    if (!props.pageModel) {
        return null;
    }

    return <App defaultPageModel={props.pageModel} />;
};

export const init = (_Page: React.ComponentType, props) => {
    // Guard against SSR environment
    if (typeof window === "undefined") {
        return;
    }

    // Try to get pageModel from window if not passed via props
    const pageModel =
        props?.defaultPageModel ||
        (window as Window & { __PAGE_MODEL__?: unknown }).__PAGE_MODEL__ ||
        null;

    const isStatic = window.IS_STATIC;

    const isNotFound = !!(window as Window & { __IS_NOT_FOUND__?: boolean })
        .__IS_NOT_FOUND__;

    const rootElement = document.getElementById("trello-portal-root");

    // Filter out custom props that shouldn't be passed to DOM elements
    const shouldForwardProp = (prop: string) => {
        const blockedProps = [
            "iconColorInitial",
            "themeInitial",
            "hoverColorInitial",
            "theme",
            "overflowingBreakpoints",
            "sectionBackground",
            "hideLanguageSelector",
            "background"
        ];
        return !blockedProps.includes(prop);
    };

    const page = (
        <StyleSheetManager shouldForwardProp={shouldForwardProp}>
            <Layout
                analyticsWebClient={null}
                location={window.location}
                userProfile={undefined}
                userProfileStatus={undefined}
                countryCode={undefined}
                hasCloudSessionTokenCookie={false}
                userAgent={navigator.userAgent}
                pageModel={pageModel}
                isNotFound={isNotFound}
            >
                {TrelloPageRenderer}
            </Layout>
        </StyleSheetManager>
    );

    if (isStatic) {
        const trelloElement = document.getElementById("trello");
        if (!trelloElement) {
            throw new Error("Trello root element not found");
        }
        const root = createRoot(trelloElement);
        root.render(page);
    } else {
        if (!rootElement || !(rootElement instanceof Element)) {
            throw new Error(
                "[init-trello] trello-portal-root is not a valid DOM element. Type: " +
                    typeof rootElement
            );
        }

        hydrateRoot(rootElement, page, {
            onRecoverableError: (error, errorInfo) => {
                // add an attribute if in CI
                if (process.env["CI"] === "true") {
                    rootElement.setAttribute("hydration-error", "true");
                }
                if (process.env["MODE"] === "dev") {
                    console.log(
                        `Recoverable Error (including Hydration): ${error}`,
                        errorInfo
                    );
                }
            }
        });
    }
};

declare global {
    interface Window {
        IS_STATIC?: boolean;
    }
}
